import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class LocationValidator {
  /**
   * Pattern for valid city and state names:
   * - Letters (a-z, A-Z)
   * - Spaces
   * - Hyphens (-)
   * - Apostrophes (')
   * - Periods (.)
   * - Commas (,)
   * - No numbers or other special characters
   * - Minimum 2 characters
   */
  static readonly CITY_STATE_PATTERN = /^[a-zA-Z\s\-'.,]+$/;

  /**
   * Validator function for city and state names
   * Allows letters, spaces, hyphens, apostrophes, periods, and commas
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static cityState(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const value = control.value.toString().trim();
    
    // Check minimum length
    if (value.length < 2) {
      return { 
        cityState: { 
          value: control.value,
          message: 'Must be at least 2 characters long'
        } 
      };
    }

    // Check if it contains only allowed characters
    if (!LocationValidator.CITY_STATE_PATTERN.test(value)) {
      return { 
        cityState: { 
          value: control.value,
          message: 'Special characters not allowed'
        } 
      };
    }

    // Check if it's all spaces or special characters
    if (value.replace(/[\s\-'.,]/g, '').length === 0) {
      return { 
        cityState: { 
          value: control.value,
          message: 'Must contain at least some letters'
        } 
      };
    }

    // Check for consecutive spaces or special characters (optional cleanup)
    if (/[\s\-'.,]{3,}/.test(value)) {
      return { 
        cityState: { 
          value: control.value,
          message: 'Too many consecutive spaces or special characters'
        } 
      };
    }

    return null; // Valid input
  }

  /**
   * Check if value is valid city/state format
   * @param value - Value to validate
   * @returns boolean
   */
  static isValidCityState(value: string): boolean {
    if (!value) return false;
    
    const control = { value: value } as AbstractControl;
    return LocationValidator.cityState(control) === null;
  }

  /**
   * Format city/state name to proper case
   * @param value - Raw value string
   * @returns Formatted value or original if invalid
   */
  static formatCityState(value: string): string {
    if (!value) return value;
    
    return value
      .trim()
      .toLowerCase()
      .split(' ')
      .map(word => {
        if (word.length === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }
}

/**
 * Factory function to create city/state validator
 * @returns ValidatorFn
 */
export function cityStateValidator(): ValidatorFn {
  return LocationValidator.cityState;
}
